import React, { FC } from 'react';
import { Box } from '@chakra-ui/react';
import 'video.js/dist/video-js.css';
import { useTranslate } from 'ts/common/hooks';
import VideoControls from './VideoControls';
import VideoInfo from './VideoInfo';
import useVideoPlayer from './useVideoPlayer';

interface Props {
    url: string;
    thumbnailUrl?: string;
    fileName?: string;
    fileSize?: string;
    onClose?: () => void;
}

const VideoPlayer: FC<Props> = ({ url, thumbnailUrl, fileName, fileSize, onClose }) => {
    const {
        containerRef,
        videoRef,
        isEnded,
        isPlaying,
        currentTime,
        duration,
        volume,
        isMuted,
        isFullscreen,
        showSettings,
        playbackSpeed,
        quality,
        handleFocus,
        handleBlur,
        handlePlayPause,
        handleSeek,
        handleToggleMute,
        handleToggleFullscreen,
        handleSpeedChange,
        handleQualityChange,
        handleReplay,
        toggleSettings
    } = useVideoPlayer({ url, thumbnailUrl, onClose });
    const t = useTranslate('studio.common.videoPlayer');

    return (
        <>
            <Box
                ref={containerRef}
                position="relative"
                w="100%"
                tabIndex={0}
                outline="none"
                onFocus={handleFocus}
                onBlur={handleBlur}
                sx={
                    isFullscreen
                        ? {
                              height: '100vh',
                              bg: 'black',
                              '& .video-js': {
                                  height: '100vh !important',
                                  paddingTop: '0 !important'
                              },
                              '& .vjs-tech': {
                                  objectFit: 'contain'
                              }
                          }
                        : undefined
                }
            >
                <div ref={videoRef} />

                {/* Transparent click-to-play overlay — above video.js element, below controls */}
                {!isEnded && (
                    <Box
                        position="absolute"
                        top={0}
                        right={0}
                        bottom={0}
                        left={0}
                        zIndex={1}
                        cursor="pointer"
                        onClick={handlePlayPause}
                    />
                )}

                {/* Custom controls overlay — visible while playing/paused */}
                {!isEnded && (
                    <VideoControls
                        isPlaying={isPlaying}
                        currentTime={currentTime}
                        duration={duration}
                        volume={volume}
                        isMuted={isMuted}
                        isFullscreen={isFullscreen}
                        showSettings={showSettings}
                        playbackSpeed={playbackSpeed}
                        quality={quality}
                        onPlayPause={handlePlayPause}
                        onSeek={handleSeek}
                        onToggleMute={handleToggleMute}
                        onToggleFullscreen={handleToggleFullscreen}
                        onToggleSettings={toggleSettings}
                        onSpeedChange={handleSpeedChange}
                        onQualityChange={handleQualityChange}
                    />
                )}

                {/* Ended overlay — thumbnail + replay button */}
                {isEnded && (
                    <Box
                        position="absolute"
                        top={0}
                        right={0}
                        bottom={0}
                        left={0}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        backgroundImage={thumbnailUrl ? `url(${thumbnailUrl})` : undefined}
                        backgroundSize="cover"
                        backgroundPosition="center"
                        bg={thumbnailUrl ? undefined : 'black'}
                    >
                        <Box
                            as="button"
                            onClick={handleReplay}
                            aria-label={t('replay')}
                            bg="blackAlpha.700"
                            border="2px solid"
                            borderColor="whiteAlpha.800"
                            borderRadius="full"
                            w={16}
                            h={16}
                            cursor="pointer"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            color="white"
                            fontSize="2xl"
                            lineHeight={1}
                        >
                            ↺
                        </Box>
                    </Box>
                )}
            </Box>

            {/* Filename + filesize — below video, hidden in fullscreen */}
            {!isFullscreen && <VideoInfo fileName={fileName} fileSize={fileSize} />}
        </>
    );
};

export default VideoPlayer;
